import React, { ChangeEvent, useEffect, useRef, useState } from "react"
import { KButton, KInput, KSpan } from "kahuna-base-react-components"
import { saveUserData } from "../../../../actions"
import Title from "../Title"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { LabelBeatport, User } from "../../../../types"
import { lang } from "../../../../constants/languages"
import { users } from "../../../../apis"
import { toast } from "react-toastify"
import { errorFilledToastOptions, infoFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { mapListToCamelCase } from "../../../../utility"

const LabelBeatportLogo = ({ user }: { user: User }) => {
  const [labelBeatportList, setLabelBeatportList] = useState<LabelBeatport[]>([])
  const [loadingLogo, setLoadingLogo] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState("")

  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchLabelCampBeatportLabels = () => {
    users(`/label_camp_beatport_labels/`, "get")
      .then((resp) => {
        if (resp.data.results) {
          setLabelBeatportList(mapListToCamelCase(resp.data.results))
        } else {
          toast.error(resp.data.message, errorFilledToastOptions)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    fetchLabelCampBeatportLabels()
  }, [user])

  const onUploadBeatportLogo = (label?: string) => {
    if (!label) return

    setSelectedLabel(label)
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedLabel) return

    const fileInput = event.target

    if (fileInput.files && fileInput.files.length > 0) {
      const file = fileInput.files[0]
      if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/jpg") {
        toast.error(lang.account.profile.error.unsupported_file_format, errorFilledToastOptions)
        return
      }

      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)

        if (img.width !== img.height) {
          toast.error(lang.account.label_beatport_logo.error_not_square, errorFilledToastOptions)
          return
        }

        const logo = new FormData()
        logo.append("logo", file)

        setLoadingLogo(true)
        toast.loading(lang.account.label_beatport_logo.uploading_logo, {
          toastId: "LOADING_LOGO",
          ...infoFilledToastOptions
        })

        users(`/upload_label_beatport_logo/?label=${selectedLabel}`, "post", logo)
          .then((resp) => {
            if (resp.data.success) {
              fetchLabelCampBeatportLabels()
              toast.success(lang.account.label_beatport_logo.success_upload_logo, successFilledToastOptions)
            } else {
              toast.error(resp.data.message, errorFilledToastOptions)
            }
            setLoadingLogo(false)
            toast.dismiss("LOADING_LOGO")
          })
          .catch(() => {
            toast.error(lang.account.label_beatport_logo.error_upload_logo, errorFilledToastOptions)
            setLoadingLogo(false)
            toast.dismiss("LOADING_LOGO")
          })
      }
      img.src = URL.createObjectURL(file)
    }
  }

  const renderLabels = () => {
    return (
      <div>
        {labelBeatportList.map((labelBeatport) => {
          return (
            <div key={`label_beatport_${labelBeatport.value}`}>
              <div className="flex justify-between mt-6 pt-6 items-center" style={{ borderTop: "1px dashed grey" }}>
                <KSpan fontWeight={500} fontSize={18} text={labelBeatport.label || ""} />
                {labelBeatport.labelBeatportLogo?.cover ? (
                  <img src={labelBeatport.labelBeatportLogo?.cover} width={128} height={128} />
                ) : (
                  <div className="items-center">
                    <KButton
                      background="transparent"
                      shadowDisabled
                      text={lang.account.label_beatport_logo.add_logo}
                      onClick={() => onUploadBeatportLogo(labelBeatport.label)}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div>
        <Title
          sectionTitle={lang.account.label_beatport_logo.title}
          sectionDetail="Edit your social media links"
          iconPath="/account_icons/link.svg"
        />
      </div>
      <div className="mt-2">
        <KSpan text={lang.account.label_beatport_logo.description} />
      </div>
      {renderLabels()}
      <input
        ref={fileInputRef}
        accept="image/png, image/jpeg"
        className="hidden"
        type="file"
        onChange={(event) => handleFileChange(event)}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return { user: state.auth.user }
}

export default connect(mapStateToProps, { saveUserData })(LabelBeatportLogo)
